import { Box, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { useMemo } from 'react';
import { api } from '../../shared/service/dataprovider';
import tokens from '../../shared/theme/tokens';
import StatusChip from '../../components/StatusChip';
import { roleBadge } from './roleBadge';

// Who took the action — chip for non-applicant actors (staff / system).
const actorTone = { ops: 'info', system: 'neutral' };
const actorLabel = { ops: 'Staff', system: 'System' };

// Full chronological history for a lease: applicant actions (head of household,
// co-applicants, AND the live-in aide — each labelled by name + role), document
// uploads, and staff decisions. Newest first. Backed by GET /v1/admin/lease/:id/audit.
export default function AuditTrailPanel({ lease }) {
  const { data, isLoading, error } = useQuery({
    queryKey: [`leaseAudit/${lease.id}`],
    queryFn: () => api.get(`/v1/admin/lease/${lease.id}/audit`),
  });

  // Resolve each event's actor to a household member (by email) so applicant
  // rows show "Aida Helper · Live-In Aide" with the matching role icon, instead
  // of an anonymous "Applicant" chip. Keyed on the lease link's people email.
  const linkByEmail = useMemo(() => {
    const m = {};
    for (const link of lease?.leaseLinks ?? []) {
      const email = link.people?.Email?.toLowerCase?.();
      if (email) m[email] = link;
    }
    return m;
  }, [lease]);

  // API returns oldest-first; show most-recent activity at the top.
  const events = useMemo(() => [...(data?.data?.events ?? [])].reverse(), [data]);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Application Audit Trail
      </Typography>

      {isLoading && <CircularProgress size={20} />}

      {error && (
        <Typography sx={{ color: tokens.colors.error.fg }}>{error?.response?.data?.message ?? 'Could not load the audit trail. Try again later.'}</Typography>
      )}

      {!isLoading && !error && events.length === 0 && <Typography sx={{ color: tokens.colors.inkFaint }}>No recorded activity for this lease yet.</Typography>}

      {events.length > 0 && (
        <Stack>
          {events.map(ev => {
            const link = ev.actorEmail ? linkByEmail[ev.actorEmail.toLowerCase()] : null;
            const person = link ? [link.people?.FirstName, link.people?.LastName].filter(Boolean).join(' ') || link.people?.FirstName : null;
            const badge = link ? roleBadge(link) : null;
            const Icon = badge?.Icon;
            return (
              <Stack key={ev.id} direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ py: 1.5, borderBottom: `1px solid ${tokens.colors.border}` }}>
                <Box sx={{ minWidth: 170, flexShrink: 0 }}>
                  <Typography variant="body2" sx={{ color: tokens.colors.inkFaint }}>
                    {ev.at ? moment(ev.at).format('MMM D, YYYY h:mm A') : '—'}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 190, flexShrink: 0 }}>
                  {person ? (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {Icon && (
                        <Tooltip title={badge.label}>
                          <Icon fontSize="small" color={badge.color} />
                        </Tooltip>
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {person}
                      </Typography>
                    </Stack>
                  ) : (
                    <StatusChip size="small" tone={actorTone[ev.actorType] ?? 'neutral'} label={actorLabel[ev.actorType] ?? 'System'} />
                  )}
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2">{ev.summary}</Typography>
                  <Typography variant="caption" sx={{ color: tokens.colors.inkFaint }}>
                    {badge ? badge.label : ev.actorEmail || '—'}
                  </Typography>
                </Box>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
